import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Paper,
  TextField,
} from '@mui/material';
import format from 'string-template';

import { StyledNotesInput } from '../OnboardingLinkModal/styles';
import { FileCategory } from '../../../../../utils/file';
import {
  StyledButton,
  StyledImg,
  StyledStack,
  StyledVideo,
} from './styles';
import texts from './texts.json';

const MsgModal = ({
  onClose,
  open,
  contactNo,
  provider,
  msgTemplate,
  msgParams,
  sendAsUserName,
}) => {
  const [message, setMessage] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const fileUrl = useMemo(() => msgTemplate?.attachmentUrl, [msgTemplate]);
  const fileCategory = useMemo(() => msgTemplate?.attachmentCategory, [msgTemplate]);

  // validate if contact number starts with a "+" sign,
  // followed by a minimum of 7 and a maximum of 15 digits
  const isValidContactNo = useMemo(() => /^\+\d{7,16}$/.test(contactNo), [contactNo]);

  useEffect(() => {
    if (msgTemplate) {
      setMessage(format(msgTemplate.msgBody, msgParams));
    }
  }, [msgParams, msgTemplate]);

  const clearAlerts = () => {
    setErrMsg('');
    setSuccessMsg('');
  };

  return (
    <Dialog
      open={open}
      PaperComponent={Paper}
    >
      <DialogTitle>
        {texts.sentMsg}
      </DialogTitle>
      <DialogContent>
        <StyledStack spacing={2} component="form">
          {(successMsg || errMsg) && (
            <Alert onClose={() => clearAlerts()} severity={errMsg ? 'error' : 'success'}>
              {successMsg || errMsg}
            </Alert>
          )}
          <FormControl fullWidth>
            <TextField
              id="sendAs"
              label={texts.sendAs}
              variant="outlined"
              value={sendAsUserName}
              disabled
            />
          </FormControl>
          <FormControl fullWidth>
            <TextField
              id="provider"
              label={texts.contactNo}
              variant="outlined"
              value={provider}
              disabled
            />
          </FormControl>
          <FormControl fullWidth>
            <TextField
              id="contact"
              label={texts.contactNo}
              variant="outlined"
              value={contactNo}
              disabled
            />
            {!isValidContactNo && (
              <FormHelperText error>
                {texts.errMsgs.invalidContact}
              </FormHelperText>
            )}
          </FormControl>
          <StyledNotesInput
            variant="outlined"
            label={texts.message}
            multiline
            rows={4}
            value={message}
            disabled
          />
          {!!fileUrl && fileCategory === FileCategory.IMAGE && (
            <StyledImg src={fileUrl} alt="media" />
          )}
          {!!fileUrl && fileCategory === FileCategory.VIDEO && (
            <StyledVideo controls>
              <source src={fileUrl} />
            </StyledVideo>
          )}
        </StyledStack>
      </DialogContent>
      <DialogActions>
        <StyledButton onClick={onClose}>Close</StyledButton>
      </DialogActions>
    </Dialog>
  );
};

MsgModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  contactNo: PropTypes.string.isRequired,
  provider: PropTypes.string,
  msgTemplate: PropTypes.object,
  msgParams: PropTypes.object,
  sendAsUserName: PropTypes.string,
};

MsgModal.defaultProps = {
  msgTemplate: null,
  msgParams: {},
  provider: null,
  sendAsUserName: '',
};

export default compose(
  observer,
)(MsgModal);
